// @flow
import React, { Component } from 'react';
import { Modal, Button, Dropdown, Grid, Form } from 'semantic-grab-ui/src';
import _ from 'lodash';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {
  DEFAULT_DATETIME_FORMAT as DATE_TIME_FORMAT,
  DEFAULT_TIME_FORMAT as TIME_FORMAT, toFormattedDate,
  toMoment, RFC3339_TIME_FORMAT,
} from 'utils/time-utils';
import MultiItemTextbox from 'components/custom/MultiItemTextbox';

type Props = {
  onSave: Function, // an object {lhs,operator,rhs} will be passed into this callback
  onCancel: Function,
  /*
  filterTypes should have the following structure:
  {
    fieldName: {
      displayName: "..."
      operators: [ {key, value, text} ... ]
      options: [ {key, value, text} ... ]
      type: "number" / "string" / "date"
    }
  }
   */
  filterTypes: Object,
  isEdit: boolean,
  open: boolean,
  lhs: string, // i.e. filter field
  operator: string,
  rhs: any, // i.e. filter value
};

// FilterDialog is like the Add Filter pop-up box in Kibana for user to set a filter
export default class FilterDialog extends Component {
  props: Props;

  componentDidMount = () => {
    this.setState({
      lhs: _.get(this.props, 'lhs', ''),
      operator: _.get(this.props, 'operator', ''),
      rhs: _.get(this.props, 'rhs', ''),
      tempOptions: [],
    });
    if (!_.get(this.props, 'isEdit', false)) {
      this.fieldInput.open();
    }
  };

  onFieldSelection = (event: any, data: any) => {
    const fieldChanged = data.value !== _.get(this.state, 'lhs', '');
    this.setState({
      lhs: data.value,
    });
    if (fieldChanged) {
      this.resetValue();
    }
    this.operatorInput.open();
  };

  onOperatorSelection = (event: any, data: any) => {
    this.setState({
      operator: data.value,
    });
    this.resetValue();
  };

  resetValue = () => {
    const op = _.get(this.state, 'operator', '');
    const fieldType = this.getFieldType(_.get(this.state, 'lhs', ''));

    let resetValue = '';
    if (op === 'in' || op === '!in') {
      resetValue = [];
    }
    if (fieldType === 'date') {
      resetValue = moment().format(RFC3339_TIME_FORMAT);
    }

    this.setState({
      rhs: resetValue,
    });
  };

  onValueSelection = (event: any, data: any) => {
    this.setState({
      rhs: data.value,
    });
  };

  getFieldOptions = () => {
    const options = [];
    _.map(_.get(this.props, 'filterTypes'), (config, key) => {
      options.push({
        key,
        value: key,
        text: _.get(config, 'displayName', ''),
      });
    });
    return options;
  };

  getOperatorOptions = (field: string) => {
    return _.get(this.props, `filterTypes.${field}.operators`, []);
  };

  getFieldType = (field: string) => {
    return _.get(this.props, `filterTypes.${field}.type`, 'string');
  };

  save = () => {
    const validationErr = this.validateFilter();
    if (!_.isEmpty(validationErr)) {
      alert(validationErr);
      return;
    }
    this.props.onSave({
      lhs: this.state.lhs,
      operator: this.state.operator,
      rhs: this.state.rhs,
    });
  };

  // validateFilter returns validation error message or empty string if there is no error
  validateFilter = () => {
    if (_.isEmpty(this.state.lhs) || _.isEmpty(this.state.operator) ||
      (!_.isNumber(this.state.rhs) && _.isEmpty(this.state.rhs))) {
      return 'filter is incomplete';
    }
    // validate data type if number is required
    if (this.getFieldType(this.state.lhs) === 'number') {
      if (!_.isArray(this.state.rhs) && _.isNaN(Number(this.state.rhs))) {
        return `${this.state.rhs} is not a number`;
      }
      if (_.isArray(this.state.rhs) && _.some(this.state.rhs, (val) => { return _.isNaN(Number(val)); })) {
        return `${this.state.rhs} are not all numbers`;
      }
    }

    return '';
  };

  cancel = () => {
    this.props.onCancel();
  };

  // getValueSelectionType determines proper value selection UI to show based on operator and field's data type
  getValueSelectionType = (field: string, operator: string, valueOptions: any) => {
    let valueSelectionType = 'none';
    const fieldDataType = _.get(this.props, `filterTypes.${field}.type`, 'string');
    const isValueArray = _.includes(['in', '!in'], operator);

    if (!_.isEmpty(field) && !_.isEmpty(operator)) {
      if (fieldDataType === 'date') {
        valueSelectionType = 'date';
      } else if (!_.isEmpty(valueOptions)) {
        valueSelectionType = 'select';
      } else if (isValueArray) {
        valueSelectionType = 'select-no-option';
      } else {
        valueSelectionType = 'text';
      }
    }
    return valueSelectionType;
  };

  render() {
    const lhs = _.get(this.state, 'lhs', '');
    const operator = _.get(this.state, 'operator', '');
    const valueOptions = _.get(this.props, `filterTypes.${lhs}.options`, []);

    const valueSelectionType = this.getValueSelectionType(lhs, operator, valueOptions);

    return (
      <Modal dimmer="inverted" open={_.get(this.props, 'open', false)}>
        <Modal.Header>Filter</Modal.Header>
        <Modal.Content>
          <Grid columns="three" divided>
            <Grid.Column>
              <Dropdown
                placeholder="Field"
                search
                selection
                options={this.getFieldOptions()}
                value={lhs}
                onChange={this.onFieldSelection}
                ref={(dropdown) => { this.fieldInput = dropdown; }}
              />
            </Grid.Column>
            <Grid.Column>
              <Dropdown
                placeholder="Operator"
                search
                selection
                options={this.getOperatorOptions(_.get(this.state, 'lhs', ''))}
                value={operator}
                onChange={this.onOperatorSelection}
                ref={(dropdown) => { this.operatorInput = dropdown; }}
              />
            </Grid.Column>
            <Grid.Column>
              {(() => {
                switch (valueSelectionType) {
                  case 'date': return (
                    <Form.Field>
                      <Form.Input
                        name="startTime"
                        label="time"
                        control={DatePicker}
                        showTimeSelect
                        timeIntervals={15}
                        dateFormat={DATE_TIME_FORMAT}
                        timeFormat={TIME_FORMAT}
                        selected={this.state.rhs && toMoment(this.state.rhs, RFC3339_TIME_FORMAT)}
                        onChange={date => this.onValueSelection({}, { name: 'startTime', value: toFormattedDate(date, RFC3339_TIME_FORMAT) })}
                      />
                    </Form.Field>
                  );
                  case 'select': return (
                    <Dropdown
                      placeholder="Value"
                      search
                      selection
                      multiple={_.includes(['in', '!in'], operator)}
                      options={valueOptions}
                      value={_.get(this.state, 'rhs', '')}
                      onChange={this.onValueSelection}
                    />
                  );
                  case 'select-no-option': return (
                    <MultiItemTextbox
                      placeholder="Value"
                      values={_.get(this.state, 'rhs', [])}
                      onChange={this.onValueSelection}
                    />
                  );
                  case 'text': return (
                    <Form.Input
                      name="rhs"
                      type="text"
                      value={_.get(this.state, 'rhs', '')}
                      onChange={this.onValueSelection}
                    />
                  );
                  default: return '';
                }
              })()}
            </Grid.Column>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button color="green" onClick={this.save}>
              Save
          </Button>
          <Button color="black" onClick={this.cancel}>
              Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}
